import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, GraduationCap, Star } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Profile() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProgress = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/lessons/user/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const completedLessons = progress.filter((p) => p.isCompleted).length;
  const totalScore = progress.reduce((sum, p) => sum + (p.score || 0), 0);
  const initials = (user?.fullName || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-8 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-16 w-16 rounded-full bg-brand-700 text-white flex items-center justify-center font-display text-xl font-semibold flex-shrink-0">
              {initials}
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-stone-800 dark:text-stone-100">{user?.fullName}</h1>
              <p className="text-stone-500 dark:text-stone-400 text-sm">{user?.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-stone-100 dark:border-stone-800 text-sm">
            <div>
              <p className="text-stone-400 dark:text-stone-500 font-medium mb-0.5">{t('profile.fullName')}</p>
              <p className="text-stone-700 dark:text-stone-200 font-medium">{user?.fullName}</p>
            </div>
            <div>
              <p className="text-stone-400 dark:text-stone-500 font-medium mb-0.5">{t('profile.email')}</p>
              <p className="text-stone-700 dark:text-stone-200 font-medium">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-8">
          <h2 className="font-display text-xl font-semibold text-stone-800 dark:text-stone-100 mb-5">{t('profile.statistics')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="rounded-xl bg-brand-50 dark:bg-brand-900/30 p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-white dark:bg-stone-800 text-brand-700 dark:text-brand-300 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={22} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-brand-800 dark:text-brand-200 leading-none mb-1">{completedLessons}</p>
                <p className="text-xs font-medium text-brand-700/70 dark:text-brand-300/70">{t('profile.completedLessons')}</p>
              </div>
            </div>

            <div className="rounded-xl bg-accent-50 dark:bg-accent-900/30 p-5 flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-white dark:bg-stone-800 text-accent-600 dark:text-accent-300 flex items-center justify-center flex-shrink-0">
                <Star size={22} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-accent-700 dark:text-accent-300 leading-none mb-1">{totalScore}</p>
                <p className="text-xs font-medium text-accent-700/70 dark:text-accent-300/70">{t('profile.totalScore')}</p>
              </div>
            </div>
          </div>

          {loading ? (
            <p className="text-center text-stone-500 dark:text-stone-400 py-6">Loading progress...</p>
          ) : progress.length === 0 ? (
            <p className="text-center text-stone-500 dark:text-stone-400 py-6">No lessons completed yet. Start learning!</p>
          ) : (
            <div>
              <h3 className="font-semibold text-stone-700 dark:text-stone-300 mb-3 text-sm">Recent Activity</h3>
              <div className="space-y-2">
                {progress.map((p) => (
                  <div
                    key={p.id}
                    className="border border-stone-200 dark:border-stone-800 rounded-xl p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium text-stone-800 dark:text-stone-100">{p.Lesson?.title_uz}</p>
                      <p className="text-sm text-stone-500 dark:text-stone-400">
                        Score: {p.score} · Progress: {p.progress}%
                      </p>
                    </div>
                    {p.isCompleted ? (
                      <span className="inline-flex items-center gap-1 bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 px-3 py-1 rounded-full text-xs font-semibold">
                        <CheckCircle2 size={14} /> Completed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-300 px-3 py-1 rounded-full text-xs font-semibold">
                        <Clock size={14} /> In Progress
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
